/*
 * 戒导航 · 博客详情页「点赞」按钮（jiedh/blog-stats）
 * =============================================================================
 * v1.4 布局（用户诉求：底部左侧放统计，右侧放操作，右侧点赞、左侧数字 +1）：
 *   文章正文最底部是一整行「动作排」（CommentPost.actionItems），左右分区：
 *     · 左区 JiedhBlogStats —— 只读统计「阅读次数 · 点赞次数」（由 index.tsx 渲染）；
 *     · 右区 本组件 —— 点赞按钮，与「回复」「更多操作」并排。
 *   点击右侧按钮后，左区「点赞次数」立即 +1 / -1（见 toggle() 里的乐观更新）。
 *
 * 为什么还要自定义按钮，而不用 flarum/likes 的原生项：
 *   · 原生项是纯文字链接，且 post.canLike() 为 false（游客 / 无权限）时整项不渲染，
 *     游客连入口都看不到；本组件对游客展示入口，点击后唤起登录弹窗（LogInModal）；
 *   · 原生项已由 index.tsx 的 items.remove('like') 移除（CSS 再兜一层 display:none），
 *     避免同一排出现两个点赞入口。
 *
 * 计数口径（与后端 jiedh/blog-stats 保持一致）：
 *   展示优先取首帖 likesCount —— flarum/likes 给 Post 注册的属性，PATCH 响应会带回；
 *   缺失时回退 discussion.likeCount（本扩展通过 PostLikeCountListener 维护的冗余计数）。
 *
 * 注意：本扩展不能 import mithril（webpack 解析不到该包），JSX 由 Babel 以
 *       pragma 'm' 编译到**全局 m**，因此这里只能用 m.redraw()。
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';

/** 只声明真正用到的模型能力，避免把 fof/blog / 核心模型类型打进 bundle */

/**
 * 当前用户是否已点赞（导出给左侧统计条复用，保证两处口径一致）。
 * 判定方式与核心 LikeButton 完全一致：比较 post.likes() 里是否有当前用户对象。
 */
export function isBlogLiked(post) {
  const user = app.session.user;
  if (!post || !user || typeof post.likes !== 'function') return false;
  const likes = post.likes();
  return Array.isArray(likes) && likes.some(liker => liker === user);
}

/**
 * 展示用点赞数（导出给左侧统计条复用）。
 * 先取首帖 likesCount（响应回来的真值），再回退文章级 likeCount。
 */
export function blogLikeCount(article, post) {
  const fromPost = post && typeof post.likesCount === 'function' ? post.likesCount() : undefined;
  const raw = typeof fromPost === 'number' ? fromPost : article.attribute('likeCount');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
export default class BlogLikeButton extends Component {
  /** 请求进行中：防止连点重复提交（同时让 Button 进入 loading 态并禁用） */
  pending = false;

  /**
   * 乐观点赞状态：null 表示「以模型为准」。
   * 点赞关系的 PATCH 响应不一定 include likes 列表，若每次都从 post.likes() 推导，
   * 会出现「点完赞按钮仍显示未点赞」的错觉；这里点击后先写入本地状态保证即时反馈。
   */
  optimistic = null;
  isLiked() {
    return this.optimistic !== null ? this.optimistic : isBlogLiked(this.attrs.post);
  }
  view() {
    const liked = this.isLiked();

    // 文案取自本扩展语言包（jiedh-blog-stats.forum.like / liked），中英文都自带
    const label = extractText(app.translator.trans(liked ? 'jiedh-blog-stats.forum.liked' : 'jiedh-blog-stats.forum.like'));
    return m(Button, {
      className: `Button Button--link JiedhBlogLike${liked ? ' is-liked' : ''}`,
      icon: liked ? 'fas fa-thumbs-up' : 'far fa-thumbs-up',
      title: label,
      "aria-label": label,
      "aria-pressed": liked,
      loading: this.pending,
      onclick: this.toggle.bind(this)
    }, m("span", {
      className: "JiedhBlogLike-label"
    }, label));
  }
  async toggle(e) {
    e.preventDefault();

    // 游客：与核心「回复」按钮（DiscussionControls.replyAction）完全一致 —— 唤起登录弹窗。
    //
    // 【坑】LogInModal 在核心是**懒加载 chunk**，不能被静态 import：
    //   `import LogInModal from 'flarum/forum/components/LogInModal'` 会被编译成
    //   `flarum.reg.get('core', 'forum/components/LogInModal')`，而初始 bundle 里该模块
    //   尚未 reg.add()，于是得到 undefined，app.modal.show(undefined) 会直接抛
    //   "Cannot read properties of undefined (reading 'prototype')"（表现为点击无反应）。
    //   正确写法是传一个返回 import() 的工厂函数，交给 ModalManager 异步解析。
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      return;
    }
    const post = this.attrs.post;

    // 首帖尚未加载（骨架屏阶段）或正在提交时忽略点击
    if (!post || this.pending) return;
    const next = !this.isLiked();
    const prev = blogLikeCount(this.attrs.article, post);

    // ---- 乐观更新：先让按钮状态与左侧数字立即变化，网络回来后再以服务端真值校正 ----
    this.optimistic = next;
    this.syncRelationship(post, next);
    this.applyCount(post, next ? prev + 1 : prev - 1);
    this.pending = true;
    m.redraw();
    try {
      // 点赞 / 取消点赞的唯一入口：PATCH /api/posts/{id} { isLiked }
      // 服务端会同步更新 post_likes 与 discussions.like_count（由 PostLikeCountListener 维护）
      await post.save({
        isLiked: next
      });

      // 以服务端返回的 likesCount 为准（响应里通常带），缺失则沿用上面的乐观值
      const latest = typeof post.likesCount === 'function' ? post.likesCount() : undefined;
      this.applyCount(post, typeof latest === 'number' ? latest : next ? prev + 1 : prev - 1);
    } catch (error) {
      // 失败回滚：状态与计数都回到点击前；网络 / 权限错误由 Flarum 默认 errorHandler 弹窗
      this.optimistic = null;
      this.syncRelationship(post, !next);
      this.applyCount(post, prev);
    } finally {
      this.pending = false;
      m.redraw();
    }
  }

  /**
   * 把「点赞数」同步写回两个模型：
   *   · post.likesCount —— 左侧统计条优先读它；
   *   · article.likeCount —— 列表页 / 精选卡 / 返回列表后读的是它，不回写会看到旧值。
   * 两个 pushAttributes 都会刷新模型 freshness，从而触发动作排重新渲染。
   */
  applyCount(post, count) {
    const safe = Math.max(0, Math.round(count));
    if (typeof post.pushAttributes === 'function') {
      post.pushAttributes({
        likesCount: safe
      });
    }
    this.attrs.article.pushAttributes({
      likeCount: safe
    });
  }

  /**
   * 乐观维护 JSON:API 的 likes 关系（与核心 flarum/likes 的做法一致）。
   * 作用：让「重新创建的组件 / 其它读取 post.likes() 的地方」也不会误判点赞状态。
   */
  syncRelationship(post, liked) {
    const user = app.session.user;
    if (!user) return;
    const data = post.data ?? (post.data = {});
    const relationships = data.relationships ?? (data.relationships = {});
    const relation = relationships.likes ?? (relationships.likes = {
      data: []
    });
    const list = Array.isArray(relation.data) ? relation.data : relation.data = [];
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i]?.id === user.id()) list.splice(i, 1);
    }
    if (liked) list.unshift({
      type: 'users',
      id: user.id()
    });
  }
}
flarum.reg.add('jiedh-blog-stats', 'forum/components/BlogLikeButton', BlogLikeButton);